// ------------------------------------------------------------------
// ReportInlinePhotoGrid — a photo/`image_gallery`-type answer's resolved
// thumbnails, rendered where its value used to be a bare evidence-id string
// (story 4-83c; P06 §4).
//
// Before this story `ReportContentRow.value` / `ReportContentSection.
// tableRows` were bare strings — `inspectionAnswers.service.js`'s `render()`
// helper stringified a photo answer's evidence-id array, so a reader saw a
// literal id (`6a87b5c26b232400e1781c24`) in a `<dl>` row or a table cell,
// for every photo field. `report.service.js` now resolves those ids to real
// thumbnails (reusing `inspectionEvidence.service.js#listForInspection`, the
// same resolver the media-summary GALLERY section already uses) and this is
// the primitive both places that value can appear render them with.
//
// Distinct from `ReportMediaSummaryGrid` (the toggle-gated, SECTION-level
// gallery of every photo in the whole report) — this is the SAME kind of
// tile, at a smaller inline size, for every OTHER place a photo-type
// answer's value appears: a plain row's `<dd>` and a `kind:'table'` cell.
// Its own primitive (DOD rule 7) rather than reusing `ReportMediaSummaryGrid`
// directly: the two sit in structurally different containers (a `<dd>`/`<td>`
// vs. a full `<section>`) and this one carries no `roomName` — the field's
// own row label already says what the photo answers, a second label would
// repeat it.
//
// a11y: each tile is a real `<img>` with `alt` text from its `caption`, or a
// generic "Photo N of M" when the resolver had no caption to give it — never
// an empty `alt`, and never the evidence id or filename standing in for one.
// A tile with no `thumbnailUrl` (a captured-but-unresolvable evidence id, or
// an evidence `kind` `MEDIA_KIND_RENDERERS` does not populate yet — see
// `backend/src/config/mediaEvidenceRenderers.js`) renders a labelled
// placeholder box, never a broken `<img src="">`. `role="list"`/`"listitem"`
// on the wrapper so a screen reader announces the tile count, matching the
// group semantics `ReportMediaSummaryGrid`'s own `<figure>` list conveys.
export interface ReportInlinePhotoGridPhoto {
  thumbnailUrl: string | null;
  caption: string | null;
}

export interface ReportInlinePhotoGridProps {
  photos: ReportInlinePhotoGridPhoto[];
  className?: string;
  /** Story 4-117 — "select evidence caption content": OFF (default, byte-
   *  identical to every pre-4-117 render — a caption is `alt`-only) renders
   *  exactly as before. ON renders a visible caption line under each tile,
   *  the same data `ReportMediaSummaryGrid`'s `<figcaption>` already shows
   *  unconditionally — this brings the two into parity rather than
   *  inventing a new caption SOURCE. `pdfExport.service.js
   *  #renderInlinePhotoGrid`'s own `showCaptions` param is the PDF twin. */
  showCaptions?: boolean;
}

export function ReportInlinePhotoGrid({ photos, className = '', showCaptions = false }: ReportInlinePhotoGridProps): JSX.Element {
  return (
    <div
      className={`flex flex-wrap gap-1.5 ${className}`}
      role="list"
      aria-label={`${photos.length} ${photos.length === 1 ? 'photo' : 'photos'}`}
    >
      {photos.map((photo, index) => {
        const alt = photo.caption || `Photo ${index + 1} of ${photos.length}`;
        const tile = photo.thumbnailUrl ? (
          /* Story 4-128e, defect D-12 — `loading="lazy"` REMOVED here for the
             same reason, and with the same measured evidence, as the sibling
             removal in `ReportMediaSummaryGrid.tsx#MediaTile`: Paged.js waits
             for every `<img>` in the pages it has fragmented to report
             `complete` before it resolves, and a lazy image that is off-screen
             never begins loading. ONE photo whose URL fails to load produced
             every page and then never resolved — a report stuck "generating"
             for good.

             This instance is the more broadly reachable of the two: SIX call
             sites in `ReportDocument.tsx` — a plain row's `<dd>` via the
             `photo-grid` row-kind renderer, the same renderer used for
             `kind:'table'` cells, the two-column full-width rows, the gallery
             pool and the narrative layout — i.e. every place a photo-type
             ANSWER appears, which is the common way a real template carries
             evidence. (The two committed reference fixtures do NOT reach it:
             every `rows[].photos` in them is `null`. That is exactly why it
             survived the first pass of this fix, which measured the
             media-summary grid the fixtures could exercise.)

             Measured the same way as the sibling, with the render asserted to
             have really produced the grid before anything was timed: 4 tiles
             with no `thumbnailUrl` 92ms · 4 valid `data:` URIs 92ms · 16 valid
             112ms · ONE malformed `data:` URI ∞ · 4 unreachable `https` URLs ∞.
             With the attribute removed, the same two cases complete in 93ms and
             100ms. Full table: `docs/prd/reference/4-128-report-renderer/
             defects.md` D-12. */
          <img src={photo.thumbnailUrl} alt={alt} className="h-full w-full object-cover" />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center px-0.5 text-center text-[7px] leading-tight text-muted-foreground"
            role="img"
            aria-label={alt}
          >
            No preview
          </div>
        );
        return (
          <div key={`${photo.thumbnailUrl ?? 'photo'}-${index}`} role="listitem" className="flex w-14 shrink-0 flex-col items-center gap-1">
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted">{tile}</div>
            {showCaptions && photo.caption && (
              <p className="w-full truncate text-center text-[9px] leading-tight text-muted-foreground" title={photo.caption}>
                {photo.caption}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
